import React, { useState, useEffect, useRef } from 'react';
import { IS_B2B } from '@config';
import Text from '@text';
import { Row, Col } from '../../util_grid/grid.js';
import { Icon } from 'audamatic-ui';
import { Loader } from '@googlemaps/js-api-loader';
import { GOOGLE_MAPS_API_KEY, GOOGLE_MAPS_ID } from '@constants';
import { useApp } from '../../../ssr/shared/application-context.js';
import marker from '@active-channel/images/map-marker.png';
import Skeleton from '@components/ui_skeleton/component.jsx';
import Usp from '@components/ui_usp/component';
import Typeset from '@components/base_typeset/component.jsx';
import PropTypes from 'prop-types';
import ChooseStoreButton from '@components/choose-store-button/component.jsx';

import './styles.scss';

const PreferredStore = (props) => {
  if (IS_B2B) {
    return null;
  }
  const { showMap = false } = props;
  const appCtx = useApp();
  const mapRef = useRef(null);

  const [currentDay, setCurrentDay] = useState('');

  const { preferredStoreId, loadingPreferredStoreId, stores, fetchStores, getPreferredStoreId } = appCtx;

  useEffect(() => {
    const today = (new Date()).getDay();
    setCurrentDay(today);

    if (!preferredStoreId) {
      getPreferredStoreId()
    }
  }, []);

  useEffect(() => {
    if (!stores) {
      fetchStores();
    }
  }, [preferredStoreId]);

  const renderUspBlock = () => {
    return (
      <div className="usp-block">
        <Usp icon="check"><Text>myenv.preferredStore.usp1</Text></Usp>
        <Usp icon="check"><Text>myenv.preferredStore.usp2</Text></Usp>
        <Usp icon="check"><Text>myenv.preferredStore.usp3</Text></Usp>
        <Usp icon="check"><Text>myenv.preferredStore.usp4</Text></Usp>
      </div>
    );
  };

  const renderNoStoreSelected = () => {
    return (
      <div className="no-store-selected">
        <h4><Text>tba.form.RegisterUser_Block_PreferredStore_Title</Text></h4>
        <div className="flex">
          {renderUspBlock()}
          <ChooseStoreButton
            textKey="myenv.preferredStore.buttonText.choose"
            variant="primary"
          />
        </div>
      </div>
    );
  };

  const loadGoogleMap = (selectedStore) => {
    const loader = new Loader({
      apiKey: GOOGLE_MAPS_API_KEY,
      version: 'weekly',
      libraries: ['maps', 'marker'],
    });

    const position = {
      lat: Number(selectedStore.attributes.latitude),
      lng: Number(selectedStore.attributes.longitude),
    };

    const mapOptions = {
      mapId: GOOGLE_MAPS_ID,
      center: position,
      zoom: 12,
      disableDefaultUI: true,
    };

    loader
      .load()
      .then((google) => {
        if (!mapRef.current) {
          return null;
        }

        const map = new google.maps.Map(mapRef.current, mapOptions);

        const markerElement = document.createElement('div');
        markerElement.className = 'custom-marker';
        markerElement.innerHTML = `<img src="${marker}" alt="Store Marker" style="pointer-events: none;"/>`;

        new google.maps.marker.AdvancedMarkerElement({
          position,
          map,
          content: markerElement,
        });
      })
      .catch((error) => {
        console.error('Error loading Google Maps:', error);
      });
  };

  const renderStoreSelected = () => {
    const selectedStore = stores?.find(store => preferredStoreId === store.storeId);
    if (!selectedStore) {
      return null;
    }
    const { attributes, phoneBusiness } = selectedStore;
    const { openMonday, openTuesday, openWednesday, openThursday, openFriday, openSaturday, openSunday } = attributes;

    if (!!showMap) {
      loadGoogleMap(selectedStore);
    }

    return (
      <div className="store-selected">
        <div className="store-data">
          <div className="flex-item">
            <h4><Text>tba.form.RegisterUser_Block_PreferredStore_Title</Text></h4>
            <div className="address">
              <Typeset tag="div" tight bold>{selectedStore.name}</Typeset>
              <Typeset tag="div" tight>{selectedStore.address}</Typeset>
              <Typeset tag="div" tight>{selectedStore.postalCode} {selectedStore.city}</Typeset>
              <ChooseStoreButton
                textKey="myenv.preferredStore.buttonText.update"
                variant="link"
              />
            </div>
          </div>
          <div className="opening-hours flex-item">
            <h4>
              <Text>storePage.openingsHours</Text>
            </h4>
            <div className="details">
              <div className={`monday ${currentDay === 1 ? 'active' : ''}`}>
                <div className="day"><Text>storePage.openingsHours.monday</Text></div>
                <div className="hours">{openMonday?.replace('uur', '')}</div>
              </div>
              <div className={`tuesday ${currentDay === 2 ? 'active' : ''}`}>
                <div className="day"><Text>storePage.openingsHours.tuesday</Text></div>
                <div className="hours">{openTuesday?.replace('uur', '')}</div>
              </div>
              <div className={`wednesday ${currentDay === 3 ? 'active' : ''}`}>
                <div className="day"><Text>storePage.openingsHours.wednesday</Text></div>
                <div className="hours">{openWednesday?.replace('uur', '')}</div>
              </div>
              <div className={`thursday ${currentDay === 4 ? 'active' : ''}`}>
                <div className="day"><Text>storePage.openingsHours.thursday</Text></div>
                <div className="hours">{openThursday?.replace('uur', '')}</div>
              </div>
              <div className={`friday ${currentDay === 5 ? 'active' : ''}`}>
                <div className="day"><Text>storePage.openingsHours.friday</Text></div>
                <div className="hours">{openFriday?.replace('uur', '')}</div>
              </div>
              <div className={`saturday ${currentDay === 6 ? 'active' : ''}`}>
                <div className="day"><Text>storePage.openingsHours.saturday</Text></div>
                <div className="hours">{openSaturday?.replace('uur', '')}</div>
              </div>
              <div className={`sunday ${currentDay === 0 ? 'active' : ''}`}>
                <div className="day"><Text>storePage.openingsHours.sunday</Text></div>
                <div className="hours">{openSunday?.replace('uur', '')}</div>
              </div>
            </div>
          </div>
        </div>
        {phoneBusiness && (
          <div className={`phone ${showMap ? 'show-map' : ''}`}>
            <Icon name="Phone" size="sm" />
            <a href={`tel:${phoneBusiness}`}>{phoneBusiness}</a>
          </div>
        )}
        {showMap && (
          <div ref={mapRef} className="gmaps">
          </div>
        )}
      </div>
    );
  };

  return (
    <Row className="preferred-store">
      <Col sm={12} className="panel desktop-panel">
        {loadingPreferredStoreId ? (
          <Skeleton height="200px" />
        ) : (
          <div className="block decorated clearfix">
            {!!preferredStoreId ? renderStoreSelected() : renderNoStoreSelected()}
          </div>
        )}
      </Col>
    </Row>
  );
};
PreferredStore.propTypes = {
  showMap: PropTypes.bool,
};
export default PreferredStore;
